import type { User, HostListing, GuestSearch, Event, Booking, Conversation, Notification } from '@rango/shared/types';

const API_BASE = '/api/v1';

interface ApiResponse<T> {
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['CF-Access-JWT-Assertion'] = this.token;
    }

    // For development mock auth
    if (import.meta.env.DEV) {
      const mockUserId = localStorage.getItem('mock_user_id');
      const mockEmail = localStorage.getItem('mock_email');
      if (mockUserId) headers['X-Mock-User-Id'] = mockUserId;
      if (mockEmail) headers['X-Mock-Email'] = mockEmail;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json() as ApiResponse<T>;

    if (!response.ok) {
      throw new ApiError(
        data.error?.code || 'UNKNOWN_ERROR',
        data.error?.message || 'An error occurred',
        response.status
      );
    }

    return data as T;
  }

  // Users
  async createUser(data: { name: string; phone?: string }) {
    return this.request<{ user: User }>('POST', '/users', data);
  }

  async getMe() {
    return this.request<{ user: User }>('GET', '/users/me');
  }

  async updateMe(data: Partial<User>) {
    return this.request<{ user: User }>('PATCH', '/users/me', data);
  }

  async updateLocation(data: { latitude: number; longitude: number; addressLine: string; city: string; state: string }) {
    return this.request<{ user: User }>('POST', '/users/me/location', data);
  }

  async submitKyc(data: { cpf: string; fullName: string; birthDate: string; documentType: string; documentNumber: string }) {
    return this.request<{ user: User }>('POST', '/users/me/kyc', data);
  }

  async updatePreferences(data: { dietaryRestrictions?: string[]; favoriteCuisines?: string[] }) {
    return this.request<{ user: User }>('POST', '/users/me/preferences', data);
  }

  // Listings (QUERO COZINHAR)
  async createListing(data: {
    location: { addressLine: string; city: string; state: string; latitude: number; longitude: number };
    dishes?: string[];
    description?: string;
    availableSeats: number;
    pricePerSeat: number;
    timeWindow: { start: string; end: string };
  }) {
    return this.request<{ listing: HostListing }>('POST', '/listings', data);
  }

  async getMyListings(params?: { status?: string; page?: number }) {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request<{ listings: HostListing[]; pagination: { page: number; pageSize: number; total: number; hasMore: boolean } }>(
      'GET',
      `/listings${query ? `?${query}` : ''}`
    );
  }

  // Searches (QUERO COMER)
  async createSearch(data: {
    location: { latitude: number; longitude: number };
    maxDistanceKm: number;
    dishes?: string[];
    maxBudget?: number;
    timeWindow: { start: string; end: string };
  }) {
    return this.request<{ search: GuestSearch }>('POST', '/searches', data);
  }

  async searchResults(params: {
    latitude: number;
    longitude: number;
    maxDistanceKm: number;
    dishes?: string;
    maxBudget?: number;
    timeWindowStart?: string;
    timeWindowEnd?: string;
  }) {
    const queryParams: Record<string, string> = {
      latitude: String(params.latitude),
      longitude: String(params.longitude),
      maxDistanceKm: String(params.maxDistanceKm),
    };
    if (params.dishes) queryParams.dishes = params.dishes;
    if (params.maxBudget) queryParams.maxBudget = String(params.maxBudget);
    if (params.timeWindowStart) queryParams.timeWindowStart = params.timeWindowStart;
    if (params.timeWindowEnd) queryParams.timeWindowEnd = params.timeWindowEnd;
    const query = new URLSearchParams(queryParams).toString();
    return this.request<{ events: Event[]; listings: HostListing[]; pagination: { page: number; pageSize: number; hasMore: boolean } }>(
      'GET',
      `/searches/results?${query}`
    );
  }

  // Events
  async getEvent(id: string) {
    return this.request<{ event: Event }>('GET', `/events/${id}`);
  }

  async createEvent(data: { hostListingId: string; dishName: string; description?: string; scheduledAt: string }) {
    return this.request<{ event: Event }>('POST', '/events', data);
  }

  // Bookings
  async createBooking(data: { eventId: string; seatsCount: number; companionIds?: string[] }) {
    return this.request<{ booking: Booking }>('POST', '/bookings', data);
  }

  async payBooking(id: string, data: { paymentMethod: 'pix' | 'credit' }) {
    return this.request<{ booking: Booking; pix?: { pixCode: string; qrCodeUrl: string; expiresAt: string } }>(
      'POST',
      `/bookings/${id}/pay`,
      data
    );
  }

  async getMyBookings(params?: { role?: 'guest' | 'host'; status?: string }) {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request<{ bookings: Booking[]; pagination: { page: number; pageSize: number; total: number; hasMore: boolean } }>(
      'GET',
      `/bookings${query ? `?${query}` : ''}`
    );
  }

  // Conversations
  async getConversations() {
    return this.request<{ conversations: Conversation[]; pagination: { page: number; pageSize: number; hasMore: boolean } }>(
      'GET',
      '/conversations'
    );
  }

  async createConversation(data: { participantId: string; contextType?: string; hostListingId?: string }) {
    return this.request<{ conversation: Conversation }>('POST', '/conversations', data);
  }

  async getConversation(id: string) {
    return this.request<{ conversation: Conversation; messages: { id: string; senderId: string; content: string; createdAt: number }[] }>(
      'GET',
      `/conversations/${id}`
    );
  }

  async sendMessage(conversationId: string, content: string) {
    return this.request<{ message: { id: string; senderId: string; content: string; createdAt: number } }>(
      'POST',
      `/conversations/${conversationId}/messages`,
      { content }
    );
  }

  // Notifications
  async getNotifications(params?: { unreadOnly?: boolean }) {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request<{ notifications: Notification[]; unreadCount: number }>(
      'GET',
      `/notifications${query ? `?${query}` : ''}`
    );
  }

  async markNotificationRead(id: string) {
    return this.request<{ success: boolean }>('PATCH', `/notifications/${id}/read`);
  }
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = new ApiClient();

